import { useState } from 'react'
import { addEmail, signOut, useAuthUser, type AuthInfo } from './auth.ts'
import { AuthError, AuthNotConfigured } from './ui.tsx'
import { syncNow } from '../data/sync.ts'

interface ProfileCardProps {
  user: AuthInfo
}

export function ProfileCard({ user }: ProfileCardProps) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [syncNote, setSyncNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submitEmail = async () => {
    setBusy(true)
    setError(null)
    const result = await addEmail(email.trim())
    if (!result.ok) setError(result.error ?? 'Could not update the email.')
    else {
      setNotice('Email updated — check the new inbox for the confirmation link.')
      setEmail('')
    }
    setBusy(false)
  }

  const doSync = async () => {
    setBusy(true)
    const result = await syncNow()
    setSyncNote(
      result === null
        ? 'Sync not configured.'
        : result.ok
          ? `Synced — ${result.pushed} pushed, ${result.pulled} pulled.`
          : `Sync failed: ${result.error ?? 'unknown error'}`,
    )
    setBusy(false)
  }

  return (
    <div className="auth-page">
      <section className="question-card">
        <h2 className="question-card-title">Profile</h2>
        <p>
          Username: <strong>{user.username ?? '(unknown)'}</strong>
        </p>
        <p>
          Email:{' '}
          {user.email && !user.email.endsWith('@users.dmat-prep.app') ? (
            user.email
          ) : (
            <span className="muted">none — without one, a forgotten password means the account is lost</span>
          )}
        </p>
        <AuthError message={error} />
        {notice ? <p className="drill-verdict correct">{notice}</p> : null}
        <label className="auth-field">
          <span>Add or change email</span>
          <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <div className="exam-results-actions">
          <button
            type="button"
            className="question-submit"
            disabled={busy || email === ''}
            onClick={() => void submitEmail()}
          >
            Save email
          </button>
          <button type="button" className="drill-exit" disabled={busy} onClick={() => void doSync()}>
            Sync now
          </button>
          <button type="button" className="drill-exit" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
        {syncNote ? <p className="muted">{syncNote}</p> : null}
      </section>
    </div>
  )
}

export function ProfilePage() {
  const { loading, configured, user } = useAuthUser()
  if (!configured) return <AuthNotConfigured />
  if (loading) return <p className="muted">Loading…</p>
  if (!user) return <p className="muted">Not signed in — <a href="/login">log in</a> first.</p>
  return <ProfileCard user={user} />
}
